import { useCallback, useEffect, useState } from "react";
import { ReactComponent as SearchIcon } from "../../../assets/svgIcon/listView/search.svg";
import { ReactComponent as Close } from "../../../assets/svgIcon/listView/Close.svg";
import { applyBoardSearchParams } from "./boardSearchParams";

/**
 * Toolbar search for shipment and purchase list/board views.
 * @param {Object} props
 * @param {Record<string, unknown>} props.listParams Shared list query params.
 * @param {Function} props.setListParams State setter for query params.
 */
export function BoardSearch({ listParams = {}, setListParams }) {
  const appliedSearch = String(listParams.search ?? "").trim();
  const [isOpen, setIsOpen] = useState(Boolean(appliedSearch));
  const [draft, setDraft] = useState(appliedSearch);

  useEffect(() => {
    setDraft(appliedSearch);
    if (appliedSearch) {
      setIsOpen(true);
    }
  }, [appliedSearch]);

  const applySearch = useCallback(
    value => {
      setListParams(state => applyBoardSearchParams(state, value));
    },
    [setListParams],
  );

  useEffect(() => {
    if (!isOpen || draft.trim() === appliedSearch) {
      return undefined;
    }
    const handle = setTimeout(() => applySearch(draft), 400);
    return () => clearTimeout(handle);
  }, [draft, isOpen, appliedSearch, applySearch]);

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      applySearch(draft);
    }
    if (event.key === "Escape") {
      setIsOpen(false);
      setDraft(appliedSearch);
    }
  };

  const clearSearch = event => {
    event?.stopPropagation?.();
    setDraft("");
    applySearch("");
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <div
        className={`milestoneBtn boardSearchTrigger${appliedSearch ? " activeMilestone" : ""}`}
        onClick={() => setIsOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        <SearchIcon aria-hidden />
        Search
        {appliedSearch ? (
          <Close
            className="boardSearchClear"
            aria-label="Clear search"
            onClick={clearSearch}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="boardSearch boardSearchOpen activeMilestone">
      <SearchIcon aria-hidden />
      <input
        className="boardSearchInput"
        type="text"
        value={draft}
        placeholder="Search name or number"
        autoFocus
        onChange={event => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (!draft.trim() && !appliedSearch) {
            setIsOpen(false);
          }
        }}
      />
      <Close
        className="boardSearchClear"
        aria-label="Clear search"
        onClick={clearSearch}
      />
    </div>
  );
}
